import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, RotateCcw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { IconRenderer } from "../lib/icons";
import { speak, chime } from "../lib/speech";
import Header from "../components/Header";
import UserNav from "../components/UserNav";

export default function FirstThenPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [stage, setStage] = useState("first"); // first | then | done

  useEffect(() => {
    (async () => {
      try {
        const pid = localStorage.getItem("rb_profile");
        const { data: profiles } = await api.get("/profiles");
        const active = profiles.find((p) => p.id === pid) || profiles[0];
        if (!active) return;
        const { data } = await api.get("/firstthen", { params: { profile_id: active.id } });
        setConfig(data.first_label ? data : { empty: true });
      } catch (e) {
        toast.error(apiError(e));
      }
    })();
  }, []);

  const finishFirst = () => {
    setStage("then");
    chime();
    speak(`Well done! Now: ${config.then_label}`);
  };

  const finishThen = () => {
    setStage("done");
    playDone();
  };

  const playDone = () => {
    chime();
    speak("All done! Well done!");
  };

  const panel = (label, icon, onDone, btnTestId) => (
    <div className="mt-8 bg-white rounded-3xl border-2 border-foreground/15 p-8 text-center">
      <span className="mx-auto w-44 h-44 rounded-3xl bg-primary/10 grid place-items-center">
        <IconRenderer name={icon} size={104} className="text-primary" />
      </span>
      <p className="mt-6 text-4xl font-extrabold leading-tight">{label}</p>
      <button
        data-testid={btnTestId}
        onClick={onDone}
        className="mt-6 w-full min-h-[80px] rounded-full bg-[#48BB78] text-white text-2xl font-extrabold flex items-center justify-center gap-3 transition-transform duration-200 ease-out hover:-translate-y-px active:scale-95"
      >
        <Check size={36} strokeWidth={3.5} /> Done
      </button>
    </div>
  );

  return (
    <div data-testid="first-then-page" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-8 pb-32">
        <button data-testid="back-button" onClick={() => navigate("/use")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>

        {!config ? (
          <p className="mt-8 text-lg text-muted-foreground">Loading…</p>
        ) : config.empty ? (
          <p data-testid="first-then-empty" className="mt-8 bg-white rounded-3xl border-2 border-dashed border-foreground/20 p-8 text-lg text-muted-foreground">
            No First–Then board set up yet. Ask your carer to set one up in Carer Mode.
          </p>
        ) : (
          <>
            <div className="mt-6 flex items-center justify-center gap-3 text-xl font-extrabold">
              <span className={`px-5 py-2 rounded-full border-2 ${stage === "first" ? "border-primary bg-primary/10" : "border-foreground/15 bg-white"}`}>First: {config.first_label}</span>
              <ArrowRight size={24} strokeWidth={3} className="text-muted-foreground" />
              <span className={`px-5 py-2 rounded-full border-2 ${stage !== "first" ? "border-[#48BB78] bg-[#48BB78]/10" : "border-foreground/15 bg-white"}`}>Then: {config.then_label}</span>
            </div>
            {stage === "first" && panel(config.first_label, config.first_icon, finishFirst, "first-done-button")}
            {stage === "then" && panel(config.then_label, config.then_icon, finishThen, "then-done-button")}
            {stage === "done" && (
              <div data-testid="first-then-done" className="mt-8 bg-[#48BB78]/15 border-2 border-[#48BB78] rounded-3xl p-8 text-center">
                <p className="text-4xl font-extrabold text-[#22543D]">All done!</p>
                <p className="mt-2 text-xl font-bold text-[#22543D]">You did {config.first_label}, then {config.then_label}.</p>
                <button
                  data-testid="first-then-restart"
                  onClick={() => setStage("first")}
                  className="mt-6 min-h-[64px] px-8 rounded-full bg-white border-2 border-foreground/25 text-xl font-bold flex items-center justify-center gap-2 mx-auto hover:bg-secondary transition-colors duration-200"
                >
                  <RotateCcw size={24} strokeWidth={2.5} /> Again
                </button>
              </div>
            )}
          </>
        )}
      </main>
      <UserNav />
    </div>
  );
}
